import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SchemaOrg from "@/components/feature/SchemaOrg";
import { aboutInfo } from "@/mocks/about";

const FORM_URL = "https://readdy.ai/api/form/d9ondjiqs38sj6jc7c20";

export default function AboutPage() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSubmitting, setFormSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormSubmitted(true);
      setFormError("");
      setTimeout(() => setFormSubmitted(false), 5000);
      return;
    }

    if (!FORM_URL) {
      setFormError("表单暂时不可用，请通过邮箱联系我们");
      return;
    }

    setFormSubmitting(true);
    setFormError("");

    try {
      const formData = new FormData(form);
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === "OK") {
        setFormSubmitted(true);
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "提交失败，请稍后再试";
        setFormError(serverMsg);
      }
    } catch {
      setFormError("网络错误，请稍后再试");
    } finally {
      setFormSubmitting(false);
    }
  };

  return (
    <>
      <SchemaOrg
        type="Organization"
        data={{
          name: "番茄漫画",
          url: "https://fanqiecomic.com",
          description: "国内领先的免费漫画在线阅读平台，提供海量韩漫、日漫、国漫高清免费阅读服务。",
          email: aboutInfo.contact.email,
          telephone: aboutInfo.contact.phone,
          address: {
            "@type": "PostalAddress",
            streetAddress: "体育西路111号番茄大厦18层",
            addressLocality: "广州",
            addressRegion: "广东",
            addressCountry: "CN",
          },
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: "关于我们 - 番茄漫画",
          url: "https://fanqiecomic.com/about",
          description: "番茄漫画品牌介绍、用户口碑评价及联系方式。免费漫画在线阅读平台，提供韩漫、日漫、国漫高清资源。",
        }}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { name: "首页", path: "/" },
            { name: "关于我们" },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50" id="brand">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 font-heading mb-3">
                关于番茄漫画
              </h1>
              <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                {aboutInfo.brandIntro.content}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="p-6 rounded-lg bg-background-100/70">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-flag-line text-primary-600 text-lg"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950">我们的使命</h3>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.brandIntro.mission}</p>
              </div>
              <div className="p-6 rounded-lg bg-background-100/70">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className="ri-eye-line text-accent-600 text-lg"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950">我们的愿景</h3>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.brandIntro.vision}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              {aboutInfo.stats.map((stat) => (
                <div key={stat.label} className="text-center p-4 rounded-lg bg-background-100/70">
                  <p className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">{stat.value}</p>
                  <p className="text-sm text-foreground-600 mt-1 whitespace-nowrap">{stat.label}</p>
                </div>
              ))}
            </div>

            <div className="mb-12">
              <h2 className="text-xl font-bold text-foreground-950 font-heading text-center mb-6">
                <i className="ri-star-line text-primary-500 mr-2"></i>核心优势
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {aboutInfo.features.map((feature) => (
                  <div key={feature.title} className="p-5 rounded-lg bg-background-100/70">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center mb-3">
                      <i className={`${feature.icon} text-accent-600 text-lg`}></i>
                    </div>
                    <h4 className="text-sm font-semibold text-foreground-950 mb-1.5">
                      <a href={`/about#${feature.title}`}>{feature.title}</a>
                    </h4>
                    <p className="text-xs text-foreground-600 leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100/60" id="reviews">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading text-center mb-8">
              <i className="ri-chat-heart-line text-primary-500 mr-2"></i>用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {aboutInfo.reviews.map((review) => (
                <div key={review.name} className="p-5 rounded-lg bg-background-50">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-background-100 flex-shrink-0">
                      <img src={review.avatar} alt={review.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{review.name}</p>
                      <div className="flex gap-0.5">
                        {Array.from({ length: review.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-yellow-400 text-xs"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-foreground-600 leading-relaxed mb-2">{review.comment}</p>
                  <p className="text-xs text-foreground-400 whitespace-nowrap">{review.date}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50" id="contact">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading text-center mb-8">
              <i className="ri-mail-send-line text-primary-500 mr-2"></i>联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">电子邮箱</p>
                    <a href={`mailto:${aboutInfo.contact.email}`} className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                      {aboutInfo.contact.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">客服电话</p>
                    <a href={`tel:${aboutInfo.contact.phone}`} className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                      {aboutInfo.contact.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-secondary-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">公司地址</p>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.address}</p>
                  </div>
                </div>
              </div>

              <div>
                {formSubmitted ? (
                  <div className="p-8 rounded-lg bg-accent-100 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-500 flex items-center justify-center">
                      <i className="ri-check-line text-background-50 text-xl"></i>
                    </div>
                    <p className="text-sm font-semibold text-accent-800 mb-1">提交成功！</p>
                    <p className="text-xs text-accent-700">感谢您的留言，我们会在24小时内回复您。</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
                    <div>
                      <label className="block text-sm font-medium text-foreground-800 mb-1">姓名</label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-800 mb-1">邮箱</label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-800 mb-1">留言内容</label>
                      <textarea
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="anti-spam-field"
                    />
                    {formError && (
                      <p className="text-sm text-red-500">{formError}</p>
                    )}
                    <button
                      type="submit"
                      disabled={formSubmitting}
                      className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50"
                    >
                      {formSubmitting ? "提交中..." : "发送留言"}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}